import { HeadingLarge, HeadingXSmall, LabelMedium, ParagraphMedium } from 'baseui/typography';

import { Block } from 'baseui/block';
import { Button } from 'baseui/button';
import { useIsDark } from '../contexts/ThemeContext';
import { useState } from 'react';
import { useStyletron } from 'baseui';
import { useTranslation } from 'react-i18next';

interface DatePoint { month: number; year: number; }
interface DateRange { start: DatePoint; end?: DatePoint; }

interface Client {
  company: string;
  companyKey?: string;
  companyUrl?: string;
  dateRange?: DateRange;
  location?: string;
  bulletKey: string;
}

interface Job {
  title: string;
  titleKey?: string;
  company: string;
  companyKey?: string;
  companyUrl?: string;
  dateRange?: DateRange;
  location: string;
  bulletKey?: string;
  clients?: Client[];
  details?: string[];
}

const JOBS: Job[] = [
  {
    title: 'Uber', companyKey: 'about.role',
    company: 'Uber', companyUrl: 'https://www.uber.com',
    dateRange: { start: { month: 9, year: 2021 } },
    location: '', bulletKey: 'experience.uber',
    details: [
      'experience.uberDetail1',
      'experience.uberDetail2',
      'experience.uberDetail3',
      'experience.uberDetail4',
      'experience.uberDetail5',
    ],
  },
  {
    title: 'Meta', companyKey: 'experience.facebookTitle',
    company: 'Meta', companyUrl: 'https://www.meta.com',
    dateRange: { start: { month: 6, year: 2019 }, end: { month: 9, year: 2021 } },
    location: '', bulletKey: 'experience.facebook',
    details: [
      'experience.metaDetail1',
      'experience.metaDetail2',
      'experience.metaDetail3',
      'experience.metaDetail4',
      'experience.metaDetail5',
    ],
  },
  {
    title: '', titleKey: 'experience.otherExperience',
    company: '', location: '',
    clients: [
      { company: 'Art in the Cape', companyUrl: 'https://www.artinthecapegallery.com/', dateRange: { start: { month: 7, year: 2018 }, end: { month: 5, year: 2019 } }, bulletKey: 'experience.artInTheCape' },
      { company: 'Menten', companyUrl: 'https://www.menten.com/', dateRange: { start: { month: 5, year: 2018 }, end: { month: 7, year: 2018 } }, bulletKey: 'experience.menten' },
      { company: 'Clean Slate Consulting', companyUrl: 'https://cleanslateconsulting.com/', dateRange: { start: { month: 2, year: 2018 }, end: { month: 6, year: 2018 } }, bulletKey: 'experience.cleanSlate' },
      { company: 'Personal portfolio', companyKey: 'experience.personalPortfolio', companyUrl: 'https://www.matthewbluestein.com/', dateRange: { start: { month: 8, year: 2016 } }, bulletKey: 'experience.portfolio' },
    ],
  },
];

function formatMonth(month: number, year: number, locale: string): string {
  return new Intl.DateTimeFormat(locale, { month: 'long', year: 'numeric' }).format(new Date(year, month - 1));
}

function PeriodLabel({ dateRange, locale, present }: { dateRange: DateRange; locale: string; present: string }) {
  const start = formatMonth(dateRange.start.month, dateRange.start.year, locale);
  const end = dateRange.end
    ? formatMonth(dateRange.end.month, dateRange.end.year, locale)
    : null;
  return (
    <LabelMedium color="contentSecondary">
      {start} – {end ?? <strong>{present}</strong>}
    </LabelMedium>
  );
}

function CompanyLink({ company, companyKey, companyUrl }: { company: string; companyKey?: string; companyUrl?: string }) {
  const { t } = useTranslation();
  const displayName = companyKey ? t(companyKey) : company;
  if (companyUrl) {
    return (
      <a
        href={companyUrl}
        target="_blank"
        rel="noopener noreferrer"
        style={{ color: 'inherit', textDecoration: 'underline', textUnderlineOffset: '3px' }}
      >
        {displayName}
      </a>
    );
  }
  return <>{displayName}</>;
}

function JobEntry({ job }: { job: Job }) {
  const [, theme] = useStyletron();
  const { t, i18n } = useTranslation();
  const isDark = useIsDark();
  const [isExpanded, setIsExpanded] = useState(false);
  return (
    <Block
      marginBottom="scale1000"
      overrides={{
        Block: {
          style: {
            borderLeft: `3px solid ${theme.colors.borderOpaque}`,
            padding: theme.sizing.scale600,
            paddingLeft: theme.sizing.scale700,
            background: isDark ? 'rgba(0,0,0,0.1)' : 'rgba(255,255,255,0.15)',
            backdropFilter: 'blur(6px)',
            WebkitBackdropFilter: 'blur(6px)',
            borderRadius: `0 ${theme.borders.radius300} ${theme.borders.radius300} 0`,
            boxShadow: '0 2px 12px rgba(0,0,0,0.05)',
          },
        },
      }}
    >
      <HeadingXSmall marginTop="0" marginBottom="scale100">
        {job.titleKey ? t(job.titleKey) : <CompanyLink company={job.title} companyUrl={job.companyUrl} />}
      </HeadingXSmall>
      <Block
        display="flex"
        justifyContent="space-between"
        alignItems="baseline"
        marginBottom="scale400"
        overrides={{ Block: { style: { flexWrap: 'wrap' } } }}
      >
        <LabelMedium>
          {job.companyKey ? t(job.companyKey) : job.company}
          {job.location ? ` · ${job.location}` : ''}
        </LabelMedium>
        {job.dateRange && (
          <PeriodLabel dateRange={job.dateRange} locale={i18n.language} present={t('experience.present')} />
        )}
      </Block>

      {job.clients ? (
        <Block>
          {job.clients.map((client) => (
            <Block key={client.company} marginBottom="scale800">
              <Block
                display="flex"
                justifyContent="space-between"
                alignItems="baseline"
                marginBottom="scale400"
                overrides={{ Block: { style: { flexWrap: 'wrap' } } }}
              >
                <LabelMedium overrides={{ Block: { style: { fontWeight: '600' } } }}>
                  <CompanyLink company={client.company} companyKey={client.companyKey} companyUrl={client.companyUrl} />
                  {client.location ? ` · ${client.location}` : ''}
                </LabelMedium>
                {client.dateRange && (
                  <PeriodLabel dateRange={client.dateRange} locale={i18n.language} present={t('experience.present')} />
                )}
              </Block>
              <ParagraphMedium marginTop="scale200" marginBottom="scale200">
                {t(client.bulletKey)}
              </ParagraphMedium>
            </Block>
          ))}
        </Block>
      ) : (
        <ParagraphMedium marginTop="scale200" marginBottom="scale200">
          {job.bulletKey ? t(job.bulletKey) : null}
        </ParagraphMedium>
      )}

      {job.details && (
        <Block marginTop="scale600">
          <Button
            kind="tertiary"
            size="compact"
            onClick={() => setIsExpanded(!isExpanded)}
          >
            {isExpanded ? `− ${t('experience.lessDetails')}` : `+ ${t('experience.moreDetails')}`}
          </Button>
          {isExpanded && (
            <Block marginTop="scale400" marginLeft="scale400">
              {job.details.map((detailKey, index) => (
                <ParagraphMedium key={index}>• {t(detailKey)}</ParagraphMedium>
              ))}
            </Block>
          )}
        </Block>
      )}
    </Block>
  );
}

interface ExperienceProps {
  onContactClick: () => void;
}

export default function Experience({ onContactClick }: ExperienceProps) {
  const { t } = useTranslation();
  return (
    <Block>
      <HeadingLarge marginBottom="scale900">{t('experience.heading')}</HeadingLarge>
      {JOBS.map((job) => (
        <JobEntry key={`${job.title}-${job.companyKey ?? job.company}`} job={job} />
      ))}
      <Block marginTop="scale800" marginBottom="scale1000" display="flex" justifyContent="center">
        <Button onClick={onContactClick}>{t('experience.getInTouch')}</Button>
      </Block>
    </Block>
  );
}
